import { FC, useState } from 'react';
import {
    Button,
    Card,
    Empty,
    Modal,
    Space,
    Switch,
    Tag,
    Typography,
} from 'antd';
import { FiEdit2, FiTrash2, FiTruck } from 'react-icons/fi';
import { FaMotorcycle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import { useCoverageContext } from './context/coverage.ctx';
import { CoverageForm } from './CoverageForm';
import { ICoverage } from '../../../interfaces/coverage.interface';

const CARD_GROUP = ['CARD', 'BILLING_CARD', 'BILLING_TRANSFER'];

const getVisibleMethods = (methods: string[]): string[] => {
    const visible: string[] = [];
    if (methods.includes('CASH')) visible.push('CASH');
    if (methods.some((m) => CARD_GROUP.includes(m))) visible.push('CARD');
    if (methods.includes('TERMINAL')) visible.push('TERMINAL');
    return visible;
};

const { Text } = Typography;

const CoverageList: FC = () => {
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS.COVERAGE' });

    const {
        coverages,
        selected,
        isDrawing,
        pendingPolygon,
        setSelected,
        handleDelete,
        handleToggleVisibility,
        cancelCreate,
    } = useCoverageContext();

    const [editCoverage, setEditCoverage] = useState<ICoverage | null>(null);

    const confirmDelete = (id: string) => {
        Modal.confirm({
            title: t('DELETE_CONFIRM_TITLE'),
            content: t('DELETE_CONFIRM_CONTENT'),
            okText: t('DELETE'),
            okType: 'danger',
            cancelText: t('CANCEL'),
            onOk: () => handleDelete(id),
        });
    };

    return (
        <div style={{
            width: '100%',
            height: '100%',
            background: 'var(--color-gray-4)',
            borderRadius: 8,
            padding: 12,
            overflowY: 'auto',
        }}
        >
            {coverages.length === 0 && !isDrawing && (
                <Empty description={t('NO_COVERAGES')} />
            )}

            {coverages.map((coverage) => (
                <Card
                    key={coverage._id}
                    size="small"
                    hoverable
                    style={{
                        marginBottom: 8,
                        borderColor: selected?._id === coverage._id ? 'var(--color-purple)' : undefined,
                        borderWidth: selected?._id === coverage._id ? 2 : 1,
                    }}
                    onClick={() => setSelected(coverage)}
                >
                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text strong ellipsis style={{ maxWidth: 160, fontSize: 14 }}>
                                {coverage.name || t('UNNAMED')}
                            </Text>
                            <Space size={4}>
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<FiEdit2 />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setEditCoverage(coverage);
                                    }}
                                />
                                <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<FiTrash2 />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        confirmDelete(coverage._id);
                                    }}
                                />
                            </Space>
                        </Space>

                        <Space size={[4, 4]} wrap>
                            {getVisibleMethods(coverage.paymentMethod || []).map((pm) => (
                                <Tag key={pm} style={{ fontSize: 11, margin: 0 }}>{t(`PAYMENT_METHOD_${pm}`)}</Tag>
                            ))}
                        </Space>

                        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                            <Space size={8} align="center">
                                <FiTruck size={16} style={{ color: coverage.carName?.includes('LITE') ? 'var(--color-purple)' : 'var(--color-gray-5)' }} />
                                <FaMotorcycle size={16} style={{ color: coverage.carName?.includes('BIKE') ? 'var(--color-purple)' : 'var(--color-gray-5)' }} />
                            </Space>
                            <Space size={8} align="center">
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {coverage.isVisible ? t('ACTIVE') : t('INACTIVE')}
                                </Text>
                                <Switch
                                    size="small"
                                    checked={coverage.isVisible}
                                    onChange={() => handleToggleVisibility(coverage)}
                                    onClick={(_, e) => e.stopPropagation()}
                                />
                            </Space>
                        </Space>
                    </Space>
                </Card>
            ))}
            {editCoverage && (
                <CoverageForm
                    coverage={editCoverage}
                    onClose={() => setEditCoverage(null)}
                />
            )}

            {pendingPolygon && (
                <CoverageForm
                    onClose={cancelCreate}
                />
            )}
        </div>
    );
};

export { CoverageList };
